import ReactGA from 'react-ga4';

import {
    AUTH_EVENTS,
    BIRTH_CHART_EVENTS,
    STORAGE_KEY_ACCESS_TOKEN,
    STORAGE_KEY_BC_DEVICE_ID,
    STORAGE_KEY_WIDGET_HOST,
    STORAGE_KEY_WIDGET_SOURCE
} from '../const';
import LoggerService from '../service/LoggerService';
import StorageService from '../service/StorageService';
import { gaActionTypes, gaCategoryTypes, gaLabelTypes } from './GoogleAnalytics';
import isGaDebugEnabled from './gaDebug';

// HORO-10370 — GA4 instrumentation for Veda v2.
//
// Two-tier super-property pattern:
//   Tier 1 (session-level): ReactGA.gtag('set', {...}) at session init —
//     auto-attaches to every subsequent event including GA4 auto-collected
//     ones (session_start, first_visit, page_view).
//   Tier 2 (event-level): trackEvent() wrapper injects per-event super-
//     properties so we don't bloat the persisted gtag config.
//
// Existing v1 event names ('Message Sent', 'Message Received', 'User login')
// are preserved byte-for-byte to keep GA4 historical-data continuity. Only
// the parameters change. New events use snake_case.

const gaKey = process.env.REACT_APP_GA_KEY;

// --- Storage keys -----------------------------------------------------------
// sessionStorage (per-tab) — clears when the tab closes.
const SESSION_KEY_ID = 'vedaAnalyticsSessionId';
const SESSION_KEY_STARTED_AT = 'vedaAnalyticsSessionStartedAt';

// localStorage (cross-session) — drives session_number / is_returning_user.
const STORAGE_KEY_FIRST_SESSION_AT = 'vedaAnalyticsFirstSessionAt';
const STORAGE_KEY_SESSION_NUMBER = 'vedaAnalyticsSessionNumber';
const STORAGE_KEY_LAST_SESSION_AT = 'vedaAnalyticsLastSessionAt';
const STORAGE_KEY_LOGIN_PROVIDER = 'vedaAnalyticsLoginProvider';

// --- In-memory super-property store (Tier 2) --------------------------------
// Mutated via setSuperProperty helpers; read by trackEvent() on each call.
const superProps = {
    user_state: 'anonymous',
    user_tier: 'anonymous',
    current_advisor: null,
    message_index_in_session: 0,
    session_number: 0,
    session_id: null
};

// --- Helpers ----------------------------------------------------------------

const generateUUID = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    // RFC4122 v4 polyfill — only hit on browsers without crypto.randomUUID.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};

const ssGet = (key) => {
    try {
        return window.sessionStorage.getItem(key);
    } catch {
        return null;
    }
};

const ssSet = (key, value) => {
    try {
        window.sessionStorage.setItem(key, value);
    } catch {
        // No-op — sessionStorage unavailable (private mode etc).
    }
};

const getDevice = () => {
    if (typeof navigator === 'undefined') return 'unknown';
    return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '') ? 'mobile' : 'desktop';
};

const getViewport = () => {
    if (typeof window === 'undefined') return '';
    return `${window.innerWidth}x${window.innerHeight}`;
};

const SEARCH_RE = /google\.|bing\.|yahoo\.|duckduckgo\.|baidu\./i;
const SOCIAL_RE = /facebook\.|instagram\.|tiktok\.|twitter\.|x\.com|linkedin\.|pinterest\.|snapchat\.|youtube\./i;
const SEARCH_UTM_SOURCES = /google|bing|yahoo|duckduckgo|baidu|search/i;
const SOCIAL_UTM_SOURCES = /facebook|instagram|tiktok|twitter|linkedin|pinterest|snapchat|youtube|tt|fb|ig/i;

// Priority: UTM params > document.referrer > widget origin > direct.
// Why this order: explicit UTM tagging is most reliable when present;
// referrer matching is a useful fallback; the widget origin only applies
// when the user landed from one of our embedded chat widgets.
const deriveEntrySource = () => {
    try {
        const url = new URL(window.location.href);
        const utmSource = (url.searchParams.get('utm_source') || '').toLowerCase();
        const utmMedium = (url.searchParams.get('utm_medium') || '').toLowerCase();
        if (utmSource || utmMedium) {
            if (utmMedium === 'cpc' || utmMedium === 'organic' || SEARCH_UTM_SOURCES.test(utmSource)) return 'search';
            if (utmMedium === 'social' || SOCIAL_UTM_SOURCES.test(utmSource)) return 'social';
            return 'direct';
        }
    } catch {
        // Malformed URL — fall through.
    }
    if (StorageService.get(STORAGE_KEY_WIDGET_HOST) || StorageService.get(STORAGE_KEY_WIDGET_SOURCE)) {
        return 'chat_widget';
    }
    const referrer = typeof document !== 'undefined' ? document.referrer : '';
    if (referrer) {
        if (SEARCH_RE.test(referrer)) return 'search';
        if (SOCIAL_RE.test(referrer)) return 'social';
    }
    return 'direct';
};

const getMinutesIntoSession = () => {
    const startedAt = parseInt(ssGet(SESSION_KEY_STARTED_AT) || '0', 10);
    if (!startedAt) return 0;
    return Math.floor((Date.now() - startedAt) / 60000);
};

const getSessionDurationSec = () => {
    const startedAt = parseInt(ssGet(SESSION_KEY_STARTED_AT) || '0', 10);
    if (!startedAt) return 0;
    return Math.floor((Date.now() - startedAt) / 1000);
};

const getDaysSince = (ts) => {
    if (!ts) return 0;
    return Math.floor((Date.now() - ts) / (1000 * 60 * 60 * 24));
};

// Per-message timing state — module-level so callers don't have to thread it.
let _lastUserMessageSentAt = 0;
let _lastUserMessageAtForLatency = 0;
const getTimeSincePreviousMessageSec = () => {
    if (!_lastUserMessageSentAt) return 0;
    return Math.floor((Date.now() - _lastUserMessageSentAt) / 1000);
};

// Optimistic check used at session init to pre-seed user_state before the
// async auth fetch resolves. Reads the JWT from storage, base64-decodes its
// payload, and trusts the `exp` claim only (no signature verification — we
// just want to avoid mis-tagging session_start as anonymous for the common
// case of a returning user with a still-valid token).
//
// Conservative defaults: any decode failure, missing exp, or already-expired
// token returns false so we don't optimistically over-attribute as registered.
// The full auth call corrects either way once useAuth resolves.
const hasValidJwt = () => {
    const token = StorageService.get(STORAGE_KEY_ACCESS_TOKEN);
    if (!token || typeof token !== 'string') return false;
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    try {
        // base64url → base64 → JSON. atob is base64-only, hence the manual
        // url-safe character substitution.
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (typeof payload.exp !== 'number') return false;
        return payload.exp * 1000 > Date.now();
    } catch {
        return false;
    }
};

// --- Session lifecycle (Tier 1) ---------------------------------------------

const initSession = () => {
    if (!gaKey) return;

    // Pre-seed user_state from the stored JWT so session_start tags returning
    // logged-in users correctly. Corrected later by setUserState() on
    // authUser.fulfilled / rejected if the server disagrees.
    if (hasValidJwt()) {
        superProps.user_state = 'registered';
    }

    // session_id — sessionStorage so it persists across same-tab navigation
    // and resets when the tab closes. Custom UUID is the join key for
    // heatmap + warehouse — GA4's own session_id isn't exposed to those tools.
    let sessionId = ssGet(SESSION_KEY_ID);
    let isNewSession = false;
    if (!sessionId) {
        sessionId = generateUUID();
        ssSet(SESSION_KEY_ID, sessionId);
        ssSet(SESSION_KEY_STARTED_AT, String(Date.now()));
        isNewSession = true;
    }

    // Cross-session counters.
    const previousSessionNumber = parseInt(StorageService.get(STORAGE_KEY_SESSION_NUMBER) || '0', 10);
    const isReturningUser = previousSessionNumber > 0;
    const previousLastSessionAt = parseInt(StorageService.get(STORAGE_KEY_LAST_SESSION_AT) || '0', 10);

    let sessionNumber = previousSessionNumber;
    if (isNewSession) {
        sessionNumber = previousSessionNumber + 1;
        StorageService.add(STORAGE_KEY_SESSION_NUMBER, String(sessionNumber));
        if (!StorageService.get(STORAGE_KEY_FIRST_SESSION_AT)) {
            StorageService.add(STORAGE_KEY_FIRST_SESSION_AT, String(Date.now()));
        }
    }

    superProps.session_number = sessionNumber;

    const referrer = typeof document !== 'undefined' ? document.referrer : '';
    const entrySource = deriveEntrySource();

    // Stash session_id + session_number in the in-memory superProps store
    // (NOT pushed via gtag('set')) so trackEvent attaches them per-event.
    // Reason: setting `session_id` via gtag('set') clobbers GA4's internal
    // `sid` field on every subsequent event request, which makes GA4 treat
    // those events as orphaned-from-session and drop them from DebugView
    // (still recorded in main reports). Same risk applies to
    // session_number / source / device since those collide with GA4's own
    // session/attribution machinery.
    superProps.session_id = sessionId;

    // Tier-1 super-properties — push via gtag('set'). Auto-attached to every
    // subsequent event. session_start has already been auto-fired by
    // gtag('config') in GoogleAnalytics.init() before this set runs, so the
    // session_start event itself will NOT carry these props. Only includes
    // names that don't collide with GA4 reserved fields.
    ReactGA.gtag('set', {
        user_state: superProps.user_state,
        user_tier: superProps.user_tier,
        entry_source: entrySource,
        // Stubbed — no A/B framework wired up yet.
        experiment_variants: '',
        referring_page_url: referrer,
        referring_page_type: '',
        viewport: getViewport(),
        is_returning_user: isReturningUser,
        days_since_last_session: getDaysSince(previousLastSessionAt)
    });

    // user_returned fires on session 2+.
    if (isNewSession && isReturningUser) {
        trackEvent('user_returned', {
            sessions_to_date: sessionNumber,
            days_since_first_session: getDaysSince(parseInt(StorageService.get(STORAGE_KEY_FIRST_SESSION_AT) || '0', 10))
        });
    }
    if (isNewSession) {
        StorageService.add(STORAGE_KEY_LAST_SESSION_AT, String(Date.now()));
    }
};

// --- trackEvent wrapper (Tier 2) --------------------------------------------

const trackEvent = (eventName, params = {}) => {
    if (!gaKey) return;
    ReactGA.event(eventName, {
        // veda-prefixed to avoid GA4's reserved names: `session_id` and
        // `session_number` get hijacked by gtag and used as the internal
        // ga_session_id / ga_session_number values (clobbering GA4's
        // auto-generated numeric session ID). Prefixing keeps our custom
        // UUID as a distinct event param queryable as `veda_session_id`,
        // and leaves GA4's session machinery alone.
        veda_session_id: superProps.session_id,
        veda_session_number: superProps.session_number,
        current_advisor: superProps.current_advisor,
        message_index_in_session: superProps.message_index_in_session,
        minutes_into_session: getMinutesIntoSession(),
        // HORO-10370/10426: attach per-event, NOT only via gtag('set') —
        // gtag('set', {customParam}) doesn't reliably propagate custom params
        // to GA4 events in this setup (that's why session props are injected
        // here too), which is why user_state read as "(not set)".
        user_state: superProps.user_state,
        user_tier: superProps.user_tier,
        // Per-event debug_mode flags this event for GA4 DebugView. HORO-10459:
        // shared gate with GoogleAnalytics.init — see utils/gaDebug. Previously
        // `IS_DEV`, which meant localhost only; deployed dev builds are
        // production builds, so their events never showed in DebugView.
        ...(isGaDebugEnabled() ? { debug_mode: true } : {}),
        ...params
    });
};

// --- Super-property setters -------------------------------------------------

// Baselines captured when the active advisor changes so an advisor_switch
// event can report messages_with_previous + time_with_previous_sec without
// the call site threading any extra state.
let _currentAdvisorStartedAt = 0;
let _messageIndexAtCurrentAdvisorStart = 0;

const setCurrentAdvisor = (advisor) => {
    if (superProps.current_advisor !== advisor) {
        _currentAdvisorStartedAt = Date.now();
        _messageIndexAtCurrentAdvisorStart = superProps.message_index_in_session;
    }
    superProps.current_advisor = advisor || null;
};

const setUserState = (state) => {
    superProps.user_state = state;
    if (gaKey) ReactGA.gtag('set', { user_state: state });
};

// HORO-10426: plan-tier super-property (anonymous | free | trial | paid),
// attached to every event like user_state. `paid` = active paid subscription
// (plan.isFree === false, not a trial); `trial` = isFree:false + isTrial;
// `free` = registered free; `anonymous` = not logged in.
const resolveUserTier = (plan) => {
    if (!plan) return 'anonymous';
    if (plan.isFree === false) return plan.isTrial === true ? 'trial' : 'paid';
    return 'free';
};

const setUserTier = (plan) => {
    const tier = resolveUserTier(plan);
    superProps.user_tier = tier;
    if (gaKey) ReactGA.gtag('set', { user_tier: tier });
    return tier;
};

const setLoginProvider = (provider) => {
    if (provider === 'google' || provider === 'facebook') {
        try {
            window.sessionStorage.setItem(STORAGE_KEY_LOGIN_PROVIDER, provider);
        } catch {
            // Provider tracking is best-effort; nothing else to do.
        }
    }
};

const readLoginProvider = () => {
    try {
        return window.sessionStorage.getItem(STORAGE_KEY_LOGIN_PROVIDER) || '';
    } catch {
        return '';
    }
};

// --- Event helpers ----------------------------------------------------------
// All routed through trackEvent() so Tier-2 super-properties auto-inject.

// ---- Messages (existing names — DO NOT change) ----
const messageSend = (content = '', advisorName = '') => {
    superProps.message_index_in_session += 1;
    _lastUserMessageAtForLatency = Date.now();
    trackEvent(gaActionTypes.messages.sent, {
        category: gaCategoryTypes.message,
        character_count: typeof content === 'string' ? content.length : 0,
        contains_question_mark: typeof content === 'string' ? content.includes('?') : false,
        advisor_name: advisorName || superProps.current_advisor || '',
        time_since_previous_message_sec: getTimeSincePreviousMessageSec(),
        // HORO-10376: server-side classifier ships separately.
        message_topic_classified: null
    });
    _lastUserMessageSentAt = Date.now();
};

const messageReceived = (response = '', tokens = 0) => {
    const responseLatencyMs = _lastUserMessageAtForLatency ? Date.now() - _lastUserMessageAtForLatency : 0;
    trackEvent(gaActionTypes.messages.received, {
        category: gaCategoryTypes.message,
        response_latency_ms: responseLatencyMs,
        response_token_count: typeof tokens === 'number' ? tokens : 0,
        response_contains_followup_question: typeof response === 'string' ? response.includes('?') : false,
        // HORO-10370: depends on an experience-offer classifier — defaulting to null
        // until that lands. Property is registered so dashboards can read it.
        response_contains_experience_offer: null
    });
};

// ---- Users (existing 'User login' name — DO NOT change) ----
const userLogin = (userId = '') => {
    trackEvent(gaActionTypes.user.login, {
        category: gaCategoryTypes.user,
        label: `${gaLabelTypes.successful}. UserId=${userId}`,
        identification_method: readLoginProvider() || 'google',
        session_number_at_identification: superProps.session_number
    });
};

const userLogout = (userId = '') => {
    trackEvent(gaActionTypes.user.logout, {
        category: gaCategoryTypes.user,
        label: `UserId=${userId}`
    });
};

const userSignUp = (userId = '') => {
    trackEvent(gaActionTypes.user.signup, {
        category: gaCategoryTypes.user,
        label: `UserId=${userId}`,
        identification_method: readLoginProvider() || 'google',
        session_number_at_identification: superProps.session_number
    });
};

const userDeleted = (userId = '') => {
    trackEvent(gaActionTypes.user.delete, {
        category: gaCategoryTypes.user,
        label: `UserId=${userId}`
    });
};

const userStartChat = (advisorId = '') => {
    trackEvent(gaActionTypes.user.startChat, {
        category: gaCategoryTypes.user,
        label: `AdvisorId=${advisorId}`
    });
};

// ---- HORO-10509: email/password auth flow funnel ----
// Distinct from userLogin/userSignUp above (the pre-existing macro success
// events) — these track every step's attempt/outcome for drop-off analysis.
// All fired centrally from authSlice.js's thunk pending/fulfilled/rejected
// cases and the toggleAuthModal reducer, not from the step components.
const authLoginButtonClick = () => trackEvent(AUTH_EVENTS.LOGIN_BUTTON_CLICK);
const authRegisterClick = () => trackEvent(AUTH_EVENTS.REGISTER_CLICK);
const authModalAutoOpen = (context) => trackEvent(AUTH_EVENTS.MODAL_AUTO_OPEN, { context });
const authLoginAttempt = () => trackEvent(AUTH_EVENTS.LOGIN_ATTEMPT);
const authLoginSuccess = () => trackEvent(AUTH_EVENTS.LOGIN_SUCCESS);
const authLoginFailure = (errorReason, status) => trackEvent(AUTH_EVENTS.LOGIN_FAILURE, { error_reason: errorReason, status });
const authRegisterEmailSubmit = () => trackEvent(AUTH_EVENTS.REGISTER_EMAIL_SUBMIT);
const authRegisterEmailFailure = (errorReason, status) =>
    trackEvent(AUTH_EVENTS.REGISTER_EMAIL_FAILURE, { error_reason: errorReason, status });
const authSetPasswordAttempt = () => trackEvent(AUTH_EVENTS.SET_PASSWORD_ATTEMPT);
const authSetPasswordFailure = (errorReason, status) => trackEvent(AUTH_EVENTS.SET_PASSWORD_FAILURE, { error_reason: errorReason, status });
const authCodeEmailSent = (purpose, resend) => trackEvent(AUTH_EVENTS.CODE_EMAIL_SENT, { purpose, resend });
const authVerifyCodeAttempt = () => trackEvent(AUTH_EVENTS.VERIFY_CODE_ATTEMPT);
const authVerifyCodeFailure = (errorReason, status) => trackEvent(AUTH_EVENTS.VERIFY_CODE_FAILURE, { error_reason: errorReason, status });
const authAccountCreated = () => trackEvent(AUTH_EVENTS.ACCOUNT_CREATED);

// ---- Payments (legacy events — routed through trackEvent for parity) ----
const userClickedOnTrialBanner = (title = '') => {
    trackEvent(gaActionTypes.payments.bannerType, {
        category: gaCategoryTypes.payments,
        label: `BannerTitle=${title}`
    });
};

const userTriggeredPaymentPopupInChat = (type = '') => {
    trackEvent(gaActionTypes.payments.triggerPopup, {
        category: gaCategoryTypes.payments,
        label: `PopupType=${type}`
    });
};

const userClickedOnPaymentPopupButton = (type = '') => {
    trackEvent(gaActionTypes.payments.popupButtons, {
        category: gaCategoryTypes.payments,
        label: `PaymentType=${type}`
    });
};

const userClickedOnPaymentProfileButton = (type = '') => {
    trackEvent(gaActionTypes.payments.profileButtons, {
        category: gaCategoryTypes.payments,
        label: `PaymentType=${type}`
    });
};

const userClickedOnIssueNotification = () => {
    trackEvent(gaActionTypes.payments.issueNotification, { category: gaCategoryTypes.payments });
};

const userClickedOnGooglePay = () => {
    trackEvent(gaActionTypes.payments.submitPayment.googlePay, { category: gaCategoryTypes.payments });
};

const userClickedOnApplePay = () => {
    trackEvent(gaActionTypes.payments.submitPayment.applePay, { category: gaCategoryTypes.payments });
};

const userSubmitCreditCard = () => {
    trackEvent(gaActionTypes.payments.submitPayment.creditCard, { category: gaCategoryTypes.payments });
};

// ---- Widget events (legacy) ----
const widgetViewed = (source = '') => {
    trackEvent(gaActionTypes.widget.viewed, { category: gaCategoryTypes.widget, label: `source=${source}` });
};
const widgetOpened = (advisor = '') => {
    trackEvent(gaActionTypes.widget.opened, { category: gaCategoryTypes.widget, label: `advisor=${advisor}` });
};
const widgetPromptSelected = (prompt = '') => {
    trackEvent(gaActionTypes.widget.promptSelected, { category: gaCategoryTypes.widget, label: `prompt=${prompt}` });
};
const widgetMessageSent = (messageCount = 0, advisor = '') => {
    trackEvent(gaActionTypes.widget.messageSent, {
        category: gaCategoryTypes.widget,
        label: `count=${messageCount},advisor=${advisor}`
    });
};
const widgetMessageReceived = (messageCount = 0) => {
    trackEvent(gaActionTypes.widget.messageReceived, { category: gaCategoryTypes.widget, label: `count=${messageCount}` });
};
const widgetExpandClicked = (messageCount = 0) => {
    trackEvent(gaActionTypes.widget.expandClicked, { category: gaCategoryTypes.widget, label: `count=${messageCount}` });
};
const widgetThresholdReached = (maxMessages = 0) => {
    trackEvent(gaActionTypes.widget.thresholdReached, { category: gaCategoryTypes.widget, label: `max=${maxMessages}` });
};
const widgetContinueClicked = () => {
    trackEvent(gaActionTypes.widget.continueClicked, { category: gaCategoryTypes.widget });
};
const widgetTermsAccepted = () => {
    trackEvent(gaActionTypes.widget.termsAccepted, { category: gaCategoryTypes.widget });
};
const widgetTermsDeclined = () => {
    trackEvent(gaActionTypes.widget.termsDeclined, { category: gaCategoryTypes.widget });
};

// ---- Chat-page widget-attribution helpers ----
const getWidgetHost = () => StorageService.get(STORAGE_KEY_WIDGET_HOST) || '';
const getWidgetVersion = () => {
    // Widget source strings are 'widget_v2', 'widget_v4', 'widget_v5', etc.
    // (see _redirectToOAuth/_buildRedirectUrl in each chat-widget-vN/widget.js).
    // Matching the version number directly — rather than only checking for
    // 'widget_v2' — keeps this correct as new widget versions ship instead
    // of bucketing them all into 'unknown'.
    const source = StorageService.get(STORAGE_KEY_WIDGET_SOURCE) || '';
    const match = source.match(/^widget_v(\d+)$/);
    if (match) return `v${match[1]}`;
    if (StorageService.get(STORAGE_KEY_WIDGET_HOST)) return 'v1';
    return 'unknown';
};

const accountCreated = (userId = '') => {
    trackEvent(gaActionTypes.chatPage.accountCreated, {
        category: gaCategoryTypes.chatPage,
        label: `userId=${userId}`,
        host_domain: getWidgetHost(),
        widget_version: getWidgetVersion()
    });
};
const signInPrompted = (messageCount = 0) => {
    trackEvent(gaActionTypes.chatPage.signInPrompted, {
        category: gaCategoryTypes.chatPage,
        label: `count=${messageCount}`,
        host_domain: getWidgetHost(),
        widget_version: getWidgetVersion()
    });
};
const widgetChatResumed = (messageCount = 0) => {
    trackEvent(gaActionTypes.chatPage.widgetChatResumed, {
        category: gaCategoryTypes.chatPage,
        label: `widgetMessages=${messageCount}`,
        host_domain: getWidgetHost(),
        widget_version: getWidgetVersion()
    });
};
const messageLimitReached = (limit = 0, isRegistered = false) => {
    trackEvent(gaActionTypes.chatPage.messageLimitReached, {
        category: gaCategoryTypes.chatPage,
        label: `limit=${limit},registered=${isRegistered}`,
        host_domain: getWidgetHost(),
        widget_version: getWidgetVersion()
    });
};

// ---- HORO-10370 NEW events (snake_case) ----

const advisorCardClick = (advisorName = '', positionInRail = 0, source = 'rail') => {
    trackEvent('advisor_card_click', {
        advisor_name: advisorName,
        position_in_rail: positionInRail,
        source
    });
};

const viewAllAdvisorsClick = () => {
    trackEvent('view_all_advisors_click', {});
};

// Reads the previous-advisor baselines maintained by setCurrentAdvisor() so
// the call site only has to pass the destination advisor. from_advisor is
// the current value of the super-property (i.e. the one the user is leaving).
// No-ops when there's no prior advisor or the tap targets the same one —
// callers can fire this unconditionally on every advisor tap.
const advisorSwitch = (toAdvisor = '') => {
    const fromAdvisor = superProps.current_advisor || '';
    if (!fromAdvisor || fromAdvisor === toAdvisor) return;
    const messagesWithPrevious = Math.max(0, superProps.message_index_in_session - _messageIndexAtCurrentAdvisorStart);
    const timeWithPreviousSec = _currentAdvisorStartedAt ? Math.floor((Date.now() - _currentAdvisorStartedAt) / 1000) : 0;
    trackEvent('advisor_switch', {
        from_advisor: fromAdvisor,
        to_advisor: toAdvisor,
        messages_with_previous: messagesWithPrevious,
        time_with_previous_sec: timeWithPreviousSec
    });
};

// ---- HORO-10426 — Anonymous homepage ----

const homeView = () => {
    trackEvent('home_view', {});
};

// Fired when the user enters chat from the homepage — via the input, a
// preset suggested question, or a mobile carousel card. `source` distinguishes
// them (input = typed-and-submitted, suggested_question = static prompt,
// typeahead = picked a typed-suggestion). user_state / user_tier ride along
// via the super-properties, so no need to repeat them here.
const homeChatStart = (source = 'input', advisorAlias = 'veda', hasMessage = false) => {
    trackEvent('home_chat_start', {
        source,
        advisor_alias: advisorAlias,
        has_message: hasMessage
    });
};

// Fired the first time the user focuses the homepage input in a mount —
// the "engaged the input" funnel step between home_view and a submit.
const homeInputFocus = () => {
    trackEvent('home_input_focus', {});
};

// HORO-10471 — Live Readings waitlist (fake-door demand test).
//
// This funnel IS the deliverable of the ticket: nothing behind the waitlist is
// real, so the only output is the measured drop-off from impression → join.
// `entry_point` distinguishes the two ways in ('promo_modal' | 'nav') so we can
// tell whether interest is driven by the interruption or by genuine browsing —
// they mean very different things for whether to build the feature.
const liveReadingsPromoShown = () => {
    trackEvent('live_readings_promo_shown', {});
};

const liveReadingsPromoDismissed = () => {
    trackEvent('live_readings_promo_dismissed', {});
};

// Distinct from `dismissed` on purpose. Closing via X is ambiguous — a mistap,
// being in a hurry, meaning to look later. Tapping "Not Interested" is a
// deliberate no, and it's the only clean negative signal a fake-door test can
// collect. Folding it into the dismissal count would throw that away.
const liveReadingsPromoDeclined = () => {
    trackEvent('live_readings_promo_declined', {});
};

const liveReadingsPromoCtaClick = () => {
    trackEvent('live_readings_promo_cta_click', {});
};

const liveReadingsNavClick = () => {
    trackEvent('live_readings_nav_click', {});
};

// HORO-10639 — Keen human-psychic upsell.
//
// `surface` ('chat' | 'catalog') is on all three because the two placements
// answer different questions: the chat card interrupts a conversation in
// progress, the catalog card is browsed to. A combined click rate would hide
// which one actually earns the traffic, and only one of them is worth keeping
// if they differ. user_state rides along as a super-property, so the
// anonymous-only audience isn't repeated here.
// `copyVariant` is set on the chat surface only (the catalog card has a single
// copy), so the param is omitted rather than sent empty where it means nothing.
const keenUpsellImpression = (surface, copyVariant) => {
    trackEvent('keen_upsell_impression', { surface, ...(copyVariant && { copy_variant: copyVariant }) });
};

const keenUpsellClick = (surface, copyVariant) => {
    trackEvent('keen_upsell_click', { surface, ...(copyVariant && { copy_variant: copyVariant }) });
};

// Chat only — the catalog card has no dismiss affordance. Kept separate from
// a missing impression: a card that was never seen and a card that was
// actively closed are opposite signals about whether to keep the placement.
const keenUpsellDismissed = (surface, copyVariant) => {
    trackEvent('keen_upsell_dismissed', { surface, ...(copyVariant && { copy_variant: copyVariant }) });
};

// --- HORO-10534: Profile v2 account management ------------------------------

// Your Account opened. Carries the balance at the moment of opening so the
// upsell's conversion can be read against how much allowance was left — the
// whole point of putting the offer on this screen. user_tier rides along as a
// super-property, so it isn't repeated here.
const profileAccountView = (messagesRemaining = 0, messagesAllowance = 0) => {
    trackEvent('profile_account_view', {
        messages_remaining: messagesRemaining,
        messages_allowance: messagesAllowance
    });
};

// `surface` distinguishes this upsell from the other entry points into the same
// checkout (the chat paywall, the subscription page), which is the only way to
// tell which surface actually earns the conversion.
const profileUpsellClick = (surface = 'your_account', planId = null, price = null) => {
    trackEvent('profile_upsell_click', { surface, plan_id: planId, price });
};

// Deletion funnel. Shown/cancelled/confirmed rather than a single "deleted"
// event: the drop-off between them is what says whether the warning copy is
// doing its job. `is_subscribed` because a subscriber is the case the copy is
// written for.
const profileDeletePromptShown = (isSubscribed = false) => {
    trackEvent('profile_delete_prompt_shown', { is_subscribed: isSubscribed });
};

const profileDeleteCancelled = () => {
    trackEvent('profile_delete_cancelled', {});
};

const profileDeleteConfirmed = (isSubscribed = false) => {
    trackEvent('profile_delete_confirmed', { is_subscribed: isSubscribed });
};

// HORO-10535 — Plan Management. `is_unlimited` separates the grandfathered
// capped tier from the newer uncapped plans, which is the split that matters
// once both exist; null while the plan can't be resolved to the catalogue.
const profilePlanView = (planId = null, isUnlimited = null) => {
    trackEvent('profile_plan_view', { plan_id: planId, is_unlimited: isUnlimited });
};

// The tap that opens the confirm dialog, not the cancellation itself — the
// existing popup owns what happens after.
const profileCancelClick = (planId = null) => {
    trackEvent('profile_cancel_click', { plan_id: planId });
};

// HORO-10539 — deferred plan switching. Scheduled and cancelled are the two
// client-side halves; the backend emits the third (applied) from the renewal
// cron, a whole billing period later, where no client is present to see it.
const profilePlanSwitchScheduled = (fromPlanId = null, toPlanId = null, isOneWay = null) => {
    trackEvent('profile_plan_switch_scheduled', { from_plan_id: fromPlanId, to_plan_id: toPlanId, is_one_way: isOneWay });
};

const profilePlanSwitchCancelled = (fromPlanId = null) => {
    trackEvent('profile_plan_switch_cancelled', { from_plan_id: fromPlanId });
};

// One-time message bundle bought from an account screen. `surface` separates
// the free and subscriber screens, and `message_count` separates the two
// bundles, so the 20- and 200-message offers can be read apart without
// resolving product ids after the fact.
// HORO-10541 — deletion funnel, mirroring the cancellation one. Reason ids
// only; the free-text comment is PII and never sent to analytics.
const deleteFlowKept = () => {
    trackEvent('delete_flow_kept', {});
};

const deleteFlowSkipped = () => {
    trackEvent('delete_flow_survey_skipped', {});
};

const deleteFlowConfirmed = (reasons = []) => {
    trackEvent('delete_flow_confirmed', { reasons: reasons.join(','), reason_count: reasons.length });
};

// HORO-10545 — cancellation funnel. `reasons` is the multi-select array; the
// free-text comment is deliberately never sent to analytics, it is PII.
const cancelFlowShown = () => {
    trackEvent('cancel_flow_shown', {});
};

const cancelFlowKept = () => {
    trackEvent('cancel_flow_kept', {});
};

const cancelFlowSkipped = () => {
    trackEvent('cancel_flow_survey_skipped', {});
};

const cancelFlowConfirmed = (reasons = []) => {
    trackEvent('cancel_flow_confirmed', { reasons: reasons.join(','), reason_count: reasons.length });
};

// HORO-10542 — payment history opened from the profile, as opposed to the
// standalone page reached from the Subscription page.
const profilePaymentHistoryView = () => {
    trackEvent('profile_payment_history_view', {});
};

// HORO-10538 — Payment Method opened. `instrument` is the whole point of the
// event: it is the first time we can answer how many subscribers pay by wallet
// rather than by card, which decides how much the update-payment-method flow
// needs to support. Null means the account has no stored method (free, or
// cancelled), which is itself worth counting.
const profilePaymentMethodView = (instrument = null) => {
    trackEvent('profile_payment_method_view', { instrument });
};

const profileBundleClick = (surface = null, productId = null, price = null, messageCount = null) => {
    trackEvent('profile_bundle_click', {
        surface,
        product_id: productId,
        price,
        message_count: messageCount
    });
};

// HORO-10679 — the in-chat top-up offered after the out-of-messages paywall is
// closed. Its own events rather than more `profile_bundle_click` rows: that
// name is wrong for a chat surface and it has no impression half, so there is
// no denominator to divide these clicks by. The parameters match it so the two
// surfaces can still be compared directly.
//
// Completion is deliberately absent — `conversion_event` and
// `checkout_purchase_success_view` already carry `purchase_kind: 'product'`
// and the `source` this offer sends into checkout, so counting it again here
// would double the bottom of the funnel.
const messagePackOfferShown = (productId = null, price = null, messageCount = null) => {
    trackEvent('message_pack_offer_shown', { surface: 'chat', product_id: productId, price, message_count: messageCount });
};

const messagePackOfferClick = (productId = null, price = null, messageCount = null) => {
    trackEvent('message_pack_offer_click', { surface: 'chat', product_id: productId, price, message_count: messageCount });
};

const liveReadingsWaitlistView = (entryPoint = 'nav', alreadyJoined = false) => {
    trackEvent('live_readings_waitlist_view', { entry_point: entryPoint, already_joined: alreadyJoined });
};

const liveReadingsWaitlistSubmit = (entryPoint = 'nav', emailPrefilled = false) => {
    trackEvent('live_readings_waitlist_submit', { entry_point: entryPoint, email_prefilled: emailPrefilled });
};

const liveReadingsWaitlistJoined = (entryPoint = 'nav', emailPrefilled = false) => {
    trackEvent('live_readings_waitlist_joined', { entry_point: entryPoint, email_prefilled: emailPrefilled });
};

const homeSuggestedQuestionClick = (question = '') => {
    trackEvent('home_suggested_question_click', { question });
};

const homeTypeaheadSelect = (question = '') => {
    trackEvent('home_typeahead_select', { question });
};

// ---- HORO-10459 — chat page cold start ----
//
// The ticket's goal is "increase the number of users who start a conversation
// with the advisor from the cold start", so the funnel needs both halves: how
// often the intro + chips were shown, and how often a chip actually converted
// into a first message. Without the impression event a rise in taps can't be
// told apart from a rise in traffic.
//
// `chip_kind` separates the tarot entry point from a plain conversation
// starter — they lead to materially different flows and shouldn't be pooled.
const coldStartIntroShown = (advisor = '', chipCount = 0) => {
    trackEvent('cold_start_intro_shown', { advisor, chip_count: chipCount });
};

const coldStartChipTapped = (advisor = '', chipIndex = 0, chipKind = 'message') => {
    trackEvent('cold_start_chip_tapped', { advisor, chip_index: chipIndex, chip_kind: chipKind });
};

// ---- HORO-10371 — Immersive experience lifecycle events ----
//
// Tracks one in-flight experience at a time per session — when a new
// experience starts before the previous one completed, the in-flight one
// is auto-abandoned with trigger='navigated_away'. Callers don't have to
// thread state around; they just call experienceStarted/Input/Completed
// and the tracker derives duration, last step, and abandonment for them.
const experienceTracker = {
    type: null,
    startedAt: 0,
    offeredAt: 0,
    offeredVia: null,
    lastStep: null,
    stepsCompleted: 0,
    // Set to true once experience_completed fires, so result_action /
    // result_viewed know the experience finished cleanly rather than
    // being abandoned.
    completed: false
};

const resetExperienceTracker = () => {
    experienceTracker.type = null;
    experienceTracker.startedAt = 0;
    experienceTracker.offeredAt = 0;
    experienceTracker.offeredVia = null;
    experienceTracker.lastStep = null;
    experienceTracker.stepsCompleted = 0;
    experienceTracker.completed = false;
};

const experienceOffered = (experienceType, offeredVia) => {
    // Stash offer timestamp so experience_started can compute
    // time_from_offer_to_start_sec without the caller threading it.
    experienceTracker.offeredAt = Date.now();
    experienceTracker.offeredVia = offeredVia;
    trackEvent('experience_offered', {
        experience_type: experienceType,
        offered_via: offeredVia,
        session_message_index: superProps.message_index_in_session
    });
};

const experienceStarted = (experienceType) => {
    // Auto-abandon any prior experience that didn't complete (user
    // switched flows without exiting the first cleanly). Edge case but
    // catches reality — users routinely tap Tarot, change their mind,
    // then tap Horoscope.
    if (experienceTracker.type && !experienceTracker.completed && experienceTracker.type !== experienceType) {
        experienceAbandoned('navigated_away');
    }
    experienceTracker.type = experienceType;
    experienceTracker.startedAt = Date.now();
    experienceTracker.lastStep = 'started';
    experienceTracker.stepsCompleted = 0;
    experienceTracker.completed = false;
    const timeFromOfferSec = experienceTracker.offeredAt ? Math.floor((Date.now() - experienceTracker.offeredAt) / 1000) : 0;
    trackEvent('experience_started', {
        experience_type: experienceType,
        time_from_offer_to_start_sec: timeFromOfferSec
    });
};

const experienceInputProvided = (stepName, inputType) => {
    if (!experienceTracker.type) return;
    experienceTracker.lastStep = stepName;
    experienceTracker.stepsCompleted += 1;
    trackEvent('experience_input_provided', {
        experience_type: experienceTracker.type,
        step_name: stepName,
        input_type: inputType
    });
};

const experienceAbandoned = (abandonmentTrigger) => {
    if (!experienceTracker.type || experienceTracker.completed) return;
    const timeInExperienceSec = experienceTracker.startedAt ? Math.floor((Date.now() - experienceTracker.startedAt) / 1000) : 0;
    trackEvent('experience_abandoned', {
        experience_type: experienceTracker.type,
        last_step_completed: experienceTracker.lastStep || 'started',
        time_in_experience_sec: timeInExperienceSec,
        abandonment_trigger: abandonmentTrigger
    });
    resetExperienceTracker();
};

const experienceCompleted = () => {
    if (!experienceTracker.type || experienceTracker.completed) return;
    const totalDurationSec = experienceTracker.startedAt ? Math.floor((Date.now() - experienceTracker.startedAt) / 1000) : 0;
    trackEvent('experience_completed', {
        experience_type: experienceTracker.type,
        total_duration_sec: totalDurationSec,
        steps_completed: experienceTracker.stepsCompleted
    });
    experienceTracker.completed = true;
};

const experienceResultViewed = (resultDwellTimeSec = 0, scrollDepthPct = 0) => {
    if (!experienceTracker.type) return;
    trackEvent('experience_result_viewed', {
        experience_type: experienceTracker.type,
        result_dwell_time_sec: resultDwellTimeSec,
        scroll_depth_pct: scrollDepthPct
    });
};

const experienceResultAction = (action) => {
    if (!experienceTracker.type) return;
    trackEvent('experience_result_action', {
        experience_type: experienceTracker.type,
        action
    });
    // 'dismiss' closes out the experience — clear the tracker so the next
    // start isn't treated as a flow switch.
    if (action === 'dismiss') resetExperienceTracker();
};

// ---- HORO-10356 — Birth Chart widget analytics ----
//
// Device ID: persisted UUID in localStorage, generated once at widget init.
// Session ID: minted on "Get My Chart" (form submit) — scopes the birth
// chart funnel so card-view and email events tie back to a single intent.
// Dedup: events marked 1× use an in-memory Set keyed on bc_session_id;
// a page reload or new form submit starts a fresh session.

// Backend relay for bc_* events — mirrors chat-widget-v4's trackEvent
// (src/chat-widget-v4/widget.js). The widget runs on partner-owned pages
// with no iframe isolation, so a client-side GA4 tag (ReactGA.event, gated
// on REACT_APP_GA_KEY) would run in the *partner's* window/document and
// mint phantom page_view/session_start/first_visit sessions scoped to the
// partner's cookies. Posting to the backend, which forwards via GA4
// Measurement Protocol, sends only the literal event — no auto-collected
// noise — while still landing in the same GA4 property (G-ZXHZ2KRRHY).
const BC_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const bcSendEvent = (eventName, params = {}) => {
    if (!BC_API_BASE_URL) return;
    try {
        const payload = JSON.stringify({
            event: eventName,
            params: {
                client_id: getBcDeviceId(),
                host_domain: window.location.hostname,
                page_url: window.location.pathname,
                ...params
            },
            user_agent: navigator.userAgent || ''
        });
        fetch(`${BC_API_BASE_URL}/analytics/widget-event`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: payload,
            credentials: 'omit',
            keepalive: true
        }).catch(() => {});
    } catch {
        // swallow — analytics must never break the widget
    }
};

const _bcFiredEvents = new Set();
let _bcDeviceId = null;
let _bcSessionId = null;
let _bcIntentTimestamp = 0;
let _bcSource = '';
// Runtime DebugView flag for the birth-chart widget's own tracker, which does
// not go through `trackEvent`. The widget ships as a production bundle, so QA on
// staging / host pages needs a way to opt individual page loads into GA4
// DebugView. Set via BirthChartWidget.init({ debug: true }) or ?bc_debug=1.
// (The main app has an equivalent in utils/gaDebug — `?ga_debug=1`.)
let _bcDebug = false;

const getBcDeviceId = () => {
    if (_bcDeviceId) return _bcDeviceId;
    try {
        _bcDeviceId = window.localStorage.getItem(STORAGE_KEY_BC_DEVICE_ID);
        if (!_bcDeviceId) {
            _bcDeviceId = generateUUID();
            window.localStorage.setItem(STORAGE_KEY_BC_DEVICE_ID, _bcDeviceId);
        }
    } catch {
        // Private browsing — fall back to session-only ID.
        _bcDeviceId = generateUUID();
    }
    return _bcDeviceId;
};

const bcTrackEvent = (eventName, params = {}, { once = false } = {}) => {
    if (once && _bcFiredEvents.has(eventName)) return;
    bcSendEvent(eventName, {
        bc_device_id: getBcDeviceId(),
        // '' rather than null pre-intent (impression fires before a session
        // exists) — GA4 Measurement Protocol silently drops the entire event
        // if any param value is JSON null, with no error surfaced anywhere.
        bc_session_id: _bcSessionId || '',
        bc_source: _bcSource,
        ...(_bcDebug ? { debug_mode: true } : {}),
        ...params
    });
    if (once) _bcFiredEvents.add(eventName);
};

// HORO-10360 — in-app Birth Chart flow (GA4). Distinct from the widget's bc*
// funnel above, which posts to the widget analytics endpoint; these go through
// the app's normal trackEvent.
const birthChartFlowStarted = (source) => trackEvent('bc_flow_started', { source });
const birthChartStepCompleted = (step) => trackEvent('bc_step_completed', { step });
const birthChartAuthGateShown = () => trackEvent('bc_auth_gate_shown');
const birthChartNudgeDismissed = () => trackEvent('bc_nudge_dismissed');
const birthChartRejected = (source, step) => trackEvent('bc_rejected', { source, step });
// The result side of the in-app flow. These are NOT the widget's bc* events
// (`widget_birth_chart_*`, sent to the widget analytics endpoint with a device and
// session of its own): this experience lives inside the app, so it reports through
// trackEvent like everything else here and must not show up in the widget funnel.
const birthChartLoadingSuccess = (sunSign, moonSign, risingSign) =>
    trackEvent('bc_loading_success', { sun_sign: sunSign || '', moon_sign: moonSign || '', rising_sign: risingSign || '' });
const birthChartResultsViewed = (sunSign, moonSign, risingSign) =>
    trackEvent('bc_results_viewed', { sun_sign: sunSign || '', moon_sign: moonSign || '', rising_sign: risingSign || '' });
const birthChartPlacementViewed = (placement, sign) => trackEvent('bc_placement_viewed', { placement, sign: sign || '' });
// POST /user/birth-chart round trip.
const birthChartGenerateLatency = (ms) => trackEvent('bc_generate_latency', { ms });
// Send → assistant message received, split by the CTA the reply carried.
const chatReplyLatency = (ms, ctaKind) => trackEvent('chat_reply_latency', { ms, cta_kind: ctaKind || 'none' });

// Impression — fires once per page load at widget init (before any form
// submit, so bc_session_id is null). Carries bc_device_id + bc_source,
// letting impressions roll up to a device and anchor the top of the funnel
// (impression → intent → results_viewed → results_sent).
const bcImpression = () => {
    bcTrackEvent(BIRTH_CHART_EVENTS.IMPRESSION, {}, { once: true });
};

const bcIntent = (birthCity = '', hasTimeOfBirth = false) => {
    // Mint a new session ID on each form submit.
    _bcSessionId = generateUUID();
    _bcFiredEvents.clear();
    _bcIntentTimestamp = Date.now();
    bcTrackEvent(BIRTH_CHART_EVENTS.INTENT, {
        birth_city: birthCity,
        has_time_of_birth: hasTimeOfBirth
    });
};

const bcLoadingSuccess = (sunSign, moonSign, risingSign) => {
    const latencyMs = _bcIntentTimestamp ? Date.now() - _bcIntentTimestamp : 0;
    bcTrackEvent(
        BIRTH_CHART_EVENTS.LOADING_SUCCESS,
        {
            sun_sign: sunSign,
            moon_sign: moonSign,
            rising_sign: risingSign,
            api_latency_ms: latencyMs
        },
        { once: true }
    );
};

const bcSunViewed = (sunSign) => {
    bcTrackEvent(BIRTH_CHART_EVENTS.SUN_VIEWED, { sun_sign: sunSign }, { once: true });
};

const bcMoonViewed = (moonSign) => {
    bcTrackEvent(BIRTH_CHART_EVENTS.MOON_VIEWED, { moon_sign: moonSign }, { once: true });
};

const bcRisingViewed = (risingSign) => {
    bcTrackEvent(BIRTH_CHART_EVENTS.RISING_VIEWED, { rising_sign: risingSign }, { once: true });
};

const bcResultsViewed = (sunSign, moonSign, risingSign) => {
    bcTrackEvent(
        BIRTH_CHART_EVENTS.RESULTS_VIEWED,
        {
            sun_sign: sunSign,
            moon_sign: moonSign,
            rising_sign: risingSign
        },
        { once: true }
    );
};

const bcResultsSent = (sunSign, moonSign, risingSign) => {
    bcTrackEvent(
        BIRTH_CHART_EVENTS.RESULTS_SENT,
        {
            sun_sign: sunSign,
            moon_sign: moonSign,
            rising_sign: risingSign
        },
        { once: true }
    );
};

// session_end — payload only. Trigger logic is deferred (HORO-10370): the
// team has not yet decided between beforeunload, an idle timer, or both.
// Exporting the payload builder so wiring can land in a follow-up without
// touching this file.
// eslint-disable-next-line no-unused-vars
const sessionEnd = ({ experiencesCompleted = 0, messageCount = 0 } = {}) => {
    trackEvent('session_end', {
        duration_sec: getSessionDurationSec(),
        message_count: messageCount,
        experiences_completed: experiencesCompleted
    });
    if (process.env.NODE_ENV !== 'production') {
        LoggerService.log('analytics.sessionEnd called — note: trigger wiring deferred (HORO-10370)');
    }
};

// ---- HORO-10478 — subscription checkout ----
//
// user_state (anonymous | registered) and user_tier ride along on every event
// via the super-properties in trackEvent, so the ticket's "anonymous vs
// registered" split needs no extra param here.

const checkoutPageViewed = (planId = '', planName = '', price = 0) => {
    trackEvent('checkout_page_viewed', {
        plan_id: planId,
        plan_name: planName,
        price
    });
};

const planChangeClicked = (planId = '') => {
    trackEvent('plan_change_clicked', { plan_id: planId });
};

// `method` is one of the Checkout Flow component names: applepay | googlepay | card.
const paymentMethodSelected = (method = '') => {
    trackEvent('payment_method_selected', { method });
};

// Fired when the customer leaves checkout without a completed payment. The
// backend deliberately stores nothing at session creation, so this is the only
// signal that a cart was started and dropped. `reason` distinguishes a
// navigation away from a tab/browser close.
const cartAbandoned = (planId = '', reason = 'navigation') => {
    trackEvent('cart_abandoned', {
        plan_id: planId,
        reason
    });
};

// The conversion itself — fired on Flow's onPaymentCompleted, i.e. the payment
// was accepted. Entitlement is granted server-side by the webhook.
//
// `origin` (HORO-10504) is whatever `useGoToCheckout` tagged `paymentData`
// with — currently only `'zara'`, from the Zara upsell flow — so a
// Zara-originated purchase is distinguishable from any other subscription
// purchase in GA. Empty string (not sent) for every other entry point.
// `price` is the plan's list price, and `intro_price` the lower amount actually
// charged when a customer had an introductory offer (HORO-10557) — 0 when they
// did not. Reporting list price keeps the figure comparable across customers;
// revenue read straight off `price` therefore overstates an intro purchase by
// the difference between the two.
// `source` (HORO-10635) is the upsell surface that led to checkout (see
// SUBSCRIPTION_SOURCES in const.js), so conversions can be split per surface.
const conversionEvent = (planId = '', planName = '', price = 0, method = '', origin = '', introPrice = 0, source = '') => {
    trackEvent('conversion_event', {
        plan_id: planId,
        plan_name: planName,
        price,
        intro_price: introPrice,
        method,
        origin,
        source
    });
};

// ---- HORO-10479 — checkout loading + confirmation ----

// NOTE: HORO-10479 also specifies `checkout_loading_dismissed`, for a back
// button on the loading screen. That control was dropped — a payment already
// submitted to Checkout cannot be cancelled from the browser, so the button
// could only pretend to. The event has no trigger and is intentionally absent.

const checkoutConfirmationPageView = (kind = '') => {
    trackEvent('checkout_confirmation_page_view', { purchase_kind: kind });
};

// The final step of the conversion funnel — the customer actually saw that
// their purchase succeeded. `origin` — see conversionEvent above.
const checkoutPurchaseSuccessView = (kind = '', price = 0, origin = '', source = '') => {
    trackEvent('checkout_purchase_success_view', { purchase_kind: kind, price, origin, source });
};

// HORO-10635: a paywall opened. The denominator for conversion rate per
// subscription source — fired by PaymentModal, TarotLimitModal and
// ZaraUpsellModal, since only some triggers fire `openPaymentPopup`.
const paywallViewed = (source = '') => {
    trackEvent('paywall_viewed', { source });
};

// ---- HORO-10703 — the /plans comparison page ----
//
// Its own events rather than `paywallViewed`: /plans is a page anyone can walk
// onto, including subscribers, so counting it as a paywall impression would
// inflate the denominator every other source's conversion rate is measured
// against. `viewer` separates the three audiences the page renders
// differently for — a subscriber view is not a conversion opportunity.
const plansPageView = (viewer = '') => {
    trackEvent('plans_page_view', { viewer });
};

const plansBillingToggle = (frequency = '', planId = null) => {
    trackEvent('plans_billing_toggle', { billing_frequency: frequency, plan_id: planId });
};

// `plan` is 'free' or 'premium'. `is_authenticated` is what separates a click
// that went straight to checkout from one that had to register first — the two
// convert at very different rates and are otherwise indistinguishable.
const plansCtaClick = (plan = '', planId = null, isAuthenticated = false) => {
    trackEvent('plans_cta_click', { plan, plan_id: planId, is_authenticated: isAuthenticated });
};

// The nav row into /plans. `plans_page_view` says the page was seen but not how
// it was reached, so without this the menu link cannot be told apart from a
// direct URL or a future footer link — and the menu is the only entry point
// today, which makes it the denominator for the whole page's conversion rate.
const plansNavClick = (isAuthenticated = false) => {
    trackEvent('plans_nav_click', { is_authenticated: isAuthenticated });
};

const checkoutConfirmationBackToChatClicked = (kind = '') => {
    trackEvent('checkout_confirmation_back_to_chat_clicked', { purchase_kind: kind });
};

const checkoutConfirmationCloseClicked = (kind = '') => {
    trackEvent('checkout_confirmation_close_clicked', { purchase_kind: kind });
};

// ---- HORO-10504 — Zara upsell/welcome funnel ----
//
// Previously this funnel had no dedicated events at all — a Zara subscribe
// click fired the same generic event every other paywall uses, and neither
// modal being SEEN was tracked. `is_anon` on the upsell events distinguishes
// the pick-a-plan-then-register path from the already-registered path,
// since they resolve very differently (one goes straight to checkout, the
// other via a sign-up detour).

const zaraUpsellModalViewed = (isAnon = false) => {
    trackEvent('zara_upsell_modal_viewed', { is_anon: isAnon });
};

const zaraUpsellSubscribeClicked = (isAnon = false) => {
    trackEvent('zara_upsell_subscribe_clicked', { is_anon: isAnon });
};

// HORO-10599: the register-variant CTA (anon viewer, or a registered-free
// viewer who hasn't started her trial yet reached through a link) — kept
// distinct from zaraUpsellSubscribeClicked above since it's a different
// funnel step (sign up for the free trial, not pay for a subscription).
const zaraUpsellRegisterClicked = () => {
    trackEvent('zara_upsell_register_clicked', {});
};

const zaraWelcomeModalViewed = () => {
    trackEvent('zara_welcome_modal_viewed', {});
};

// HORO-10474 — a request failed on the client. `request_id` is the server's
// x-request-id, which is what lets a GA4 row be joined to the backend log line
// for the same request. Path is sent without its query string: query values
// carry ids and occasionally tokens, and the path is what identifies the failing
// endpoint.
const apiError = ({ status = 0, method = '', path = '', requestId = '', code = '' } = {}) => {
    trackEvent(gaActionTypes.api.error, {
        category: gaCategoryTypes.api,
        status,
        method,
        path,
        request_id: requestId,
        error_code: code
    });
};

const gaEvents = {
    // Messages
    eventMessageSend: (content, advisorName) => messageSend(content, advisorName),
    eventMessageReceived: (response, tokens) => messageReceived(response, tokens),
    eventStartChat: (advisorId) => userStartChat(advisorId),
    // Users
    login: (userId) => userLogin(userId),
    logout: (userId) => userLogout(userId),
    signUp: (userId) => userSignUp(userId),
    delete: (userId) => userDeleted(userId),
    // HORO-10509 — email/password auth flow funnel
    authLoginButtonClick: () => authLoginButtonClick(),
    authRegisterClick: () => authRegisterClick(),
    authModalAutoOpen: (context) => authModalAutoOpen(context),
    authLoginAttempt: () => authLoginAttempt(),
    authLoginSuccess: () => authLoginSuccess(),
    authLoginFailure: (errorReason, status) => authLoginFailure(errorReason, status),
    authRegisterEmailSubmit: () => authRegisterEmailSubmit(),
    authRegisterEmailFailure: (errorReason, status) => authRegisterEmailFailure(errorReason, status),
    authSetPasswordAttempt: () => authSetPasswordAttempt(),
    authSetPasswordFailure: (errorReason, status) => authSetPasswordFailure(errorReason, status),
    authCodeEmailSent: (purpose, resend) => authCodeEmailSent(purpose, resend),
    authVerifyCodeAttempt: () => authVerifyCodeAttempt(),
    authVerifyCodeFailure: (errorReason, status) => authVerifyCodeFailure(errorReason, status),
    authAccountCreated: () => authAccountCreated(),
    // Payments (legacy)
    onTrialBannerClick: (title) => userClickedOnTrialBanner(title),
    openPaymentPopup: (type) => userTriggeredPaymentPopupInChat(type),
    onPaymentPopupBtn: (type) => userClickedOnPaymentPopupButton(type),
    onPaymentProfileBtn: (type) => userClickedOnPaymentProfileButton(type),
    onIssueBtn: () => userClickedOnIssueNotification(),
    onGooglePay: () => userClickedOnGooglePay(),
    onApplePay: () => userClickedOnApplePay(),
    onCreditCardPay: () => userSubmitCreditCard(),
    // Widget (legacy)
    widgetViewed: (source) => widgetViewed(source),
    widgetOpened: (advisor) => widgetOpened(advisor),
    widgetPromptSelected: (prompt) => widgetPromptSelected(prompt),
    widgetMessageSent: (count, advisor) => widgetMessageSent(count, advisor),
    widgetMessageReceived: (count) => widgetMessageReceived(count),
    widgetExpandClicked: (count) => widgetExpandClicked(count),
    widgetThresholdReached: (max) => widgetThresholdReached(max),
    widgetContinueClicked: () => widgetContinueClicked(),
    widgetTermsAccepted: () => widgetTermsAccepted(),
    widgetTermsDeclined: () => widgetTermsDeclined(),
    // Chat page (widget flow)
    accountCreated: (userId) => accountCreated(userId),
    signInPrompted: (count) => signInPrompted(count),
    widgetChatResumed: (count) => widgetChatResumed(count),
    messageLimitReached: (limit, isRegistered) => messageLimitReached(limit, isRegistered),
    // HORO-10370 — advisor roster
    advisorCardClick: (advisorName, positionInRail, source) => advisorCardClick(advisorName, positionInRail, source),
    viewAllAdvisorsClick: () => viewAllAdvisorsClick(),
    advisorSwitch: (toAdvisor) => advisorSwitch(toAdvisor),
    // HORO-10426 — anonymous homepage
    homeView: () => homeView(),
    homeChatStart: (source, advisorAlias, hasMessage) => homeChatStart(source, advisorAlias, hasMessage),
    homeSuggestedQuestionClick: (question) => homeSuggestedQuestionClick(question),
    homeTypeaheadSelect: (question) => homeTypeaheadSelect(question),
    homeInputFocus: () => homeInputFocus(),
    // HORO-10471 — Live Readings waitlist (fake-door demand test)
    liveReadingsPromoShown: () => liveReadingsPromoShown(),
    liveReadingsPromoDismissed: () => liveReadingsPromoDismissed(),
    liveReadingsPromoDeclined: () => liveReadingsPromoDeclined(),
    liveReadingsPromoCtaClick: () => liveReadingsPromoCtaClick(),
    liveReadingsNavClick: () => liveReadingsNavClick(),
    // HORO-10639 — Keen human-psychic upsell
    keenUpsellImpression: (surface, copyVariant) => keenUpsellImpression(surface, copyVariant),
    keenUpsellClick: (surface, copyVariant) => keenUpsellClick(surface, copyVariant),
    keenUpsellDismissed: (surface, copyVariant) => keenUpsellDismissed(surface, copyVariant),
    // HORO-10534 — profile account management
    profileAccountView: (remaining, allowance) => profileAccountView(remaining, allowance),
    profileUpsellClick: (surface, planId, price) => profileUpsellClick(surface, planId, price),
    profileDeletePromptShown: (isSubscribed) => profileDeletePromptShown(isSubscribed),
    profileDeleteCancelled: () => profileDeleteCancelled(),
    profileDeleteConfirmed: (isSubscribed) => profileDeleteConfirmed(isSubscribed),
    // HORO-10535 — plan management
    profilePlanView: (planId, isUnlimited) => profilePlanView(planId, isUnlimited),
    profileCancelClick: (planId) => profileCancelClick(planId),
    // HORO-10539 — plan switching
    profilePlanSwitchScheduled: (fromPlanId, toPlanId, isOneWay) => profilePlanSwitchScheduled(fromPlanId, toPlanId, isOneWay),
    profilePlanSwitchCancelled: (fromPlanId) => profilePlanSwitchCancelled(fromPlanId),
    profileBundleClick: (surface, productId, price, count) => profileBundleClick(surface, productId, price, count),
    messagePackOfferShown: (productId, price, count) => messagePackOfferShown(productId, price, count),
    messagePackOfferClick: (productId, price, count) => messagePackOfferClick(productId, price, count),
    profilePaymentHistoryView: () => profilePaymentHistoryView(),
    profilePaymentMethodView: (instrument) => profilePaymentMethodView(instrument),
    // HORO-10545 — cancellation funnel
    cancelFlowShown: () => cancelFlowShown(),
    cancelFlowKept: () => cancelFlowKept(),
    cancelFlowSkipped: () => cancelFlowSkipped(),
    cancelFlowConfirmed: (reasons) => cancelFlowConfirmed(reasons),
    // HORO-10541 — deletion funnel
    deleteFlowKept: () => deleteFlowKept(),
    deleteFlowSkipped: () => deleteFlowSkipped(),
    deleteFlowConfirmed: (reasons) => deleteFlowConfirmed(reasons),
    liveReadingsWaitlistView: (entryPoint, alreadyJoined) => liveReadingsWaitlistView(entryPoint, alreadyJoined),
    liveReadingsWaitlistSubmit: (entryPoint, emailPrefilled) => liveReadingsWaitlistSubmit(entryPoint, emailPrefilled),
    liveReadingsWaitlistJoined: (entryPoint, emailPrefilled) => liveReadingsWaitlistJoined(entryPoint, emailPrefilled),
    // HORO-10459 — v3 cold start
    coldStartIntroShown: (advisor, chipCount) => coldStartIntroShown(advisor, chipCount),
    coldStartChipTapped: (advisor, chipIndex, chipKind) => coldStartChipTapped(advisor, chipIndex, chipKind),
    // HORO-10647 — love tarot history
    loveTarotPillTapped: (viewerState) => trackEvent('love_tarot_pill_tapped', { viewer_state: viewerState }),
    loveTarotHistoryView: () => trackEvent('love_tarot_history_view'),
    loveTarotOpenChat: () => trackEvent('love_tarot_open_chat'),
    loveTarotFeedback: (feedback) => trackEvent('love_tarot_feedback', { feedback: feedback || 'cleared' }),
    // HORO-10371 — immersive experience lifecycle
    experienceOffered: (experienceType, offeredVia) => experienceOffered(experienceType, offeredVia),
    experienceStarted: (experienceType) => experienceStarted(experienceType),
    experienceInputProvided: (stepName, inputType) => experienceInputProvided(stepName, inputType),
    experienceAbandoned: (abandonmentTrigger) => experienceAbandoned(abandonmentTrigger),
    experienceCompleted: () => experienceCompleted(),
    experienceResultViewed: (dwellTimeSec, scrollDepthPct) => experienceResultViewed(dwellTimeSec, scrollDepthPct),
    experienceResultAction: (action) => experienceResultAction(action),
    // HORO-10360 — in-app Birth Chart flow
    bcFlowStarted: (source) => birthChartFlowStarted(source),
    bcStepCompleted: (step) => birthChartStepCompleted(step),
    bcAuthGateShown: () => birthChartAuthGateShown(),
    bcNudgeDismissed: () => birthChartNudgeDismissed(),
    bcRejected: (source, step) => birthChartRejected(source, step),
    bcGenerateLatency: (ms) => birthChartGenerateLatency(ms),
    birthChartLoadingSuccess: (sun, moon, rising) => birthChartLoadingSuccess(sun, moon, rising),
    birthChartResultsViewed: (sun, moon, rising) => birthChartResultsViewed(sun, moon, rising),
    birthChartPlacementViewed: (placement, sign) => birthChartPlacementViewed(placement, sign),
    chatReplyLatency: (ms, ctaKind) => chatReplyLatency(ms, ctaKind),
    // HORO-10356 — Birth Chart widget
    bcImpression: () => bcImpression(),
    bcIntent: (birthCity, hasTimeOfBirth) => bcIntent(birthCity, hasTimeOfBirth),
    bcLoadingSuccess: (sunSign, moonSign, risingSign) => bcLoadingSuccess(sunSign, moonSign, risingSign),
    bcSunViewed: (sunSign) => bcSunViewed(sunSign),
    bcMoonViewed: (moonSign) => bcMoonViewed(moonSign),
    bcRisingViewed: (risingSign) => bcRisingViewed(risingSign),
    bcResultsViewed: (sunSign, moonSign, risingSign) => bcResultsViewed(sunSign, moonSign, risingSign),
    bcResultsSent: (sunSign, moonSign, risingSign) => bcResultsSent(sunSign, moonSign, risingSign),
    setBcSource: (source) => {
        _bcSource = source || '';
    },
    setBcDebug: (enabled) => {
        _bcDebug = !!enabled;
    },
    getBcDeviceId: () => getBcDeviceId(),
    // HORO-10478 — subscription checkout
    checkoutPageViewed: (planId, planName, price) => checkoutPageViewed(planId, planName, price),
    planChangeClicked: (planId) => planChangeClicked(planId),
    paymentMethodSelected: (method) => paymentMethodSelected(method),
    cartAbandoned: (planId, reason) => cartAbandoned(planId, reason),
    conversionEvent: (planId, planName, price, method, origin, introPrice, source) =>
        conversionEvent(planId, planName, price, method, origin, introPrice, source),
    paywallViewed: (source) => paywallViewed(source),
    // HORO-10703 — the /plans comparison page
    plansPageView: (viewer) => plansPageView(viewer),
    plansBillingToggle: (frequency, planId) => plansBillingToggle(frequency, planId),
    plansCtaClick: (plan, planId, isAuthenticated) => plansCtaClick(plan, planId, isAuthenticated),
    plansNavClick: (isAuthenticated) => plansNavClick(isAuthenticated),
    // HORO-10479 — checkout loading + confirmation
    checkoutConfirmationPageView: (kind) => checkoutConfirmationPageView(kind),
    checkoutPurchaseSuccessView: (kind, price, origin, source) => checkoutPurchaseSuccessView(kind, price, origin, source),
    checkoutConfirmationBackToChatClicked: (kind) => checkoutConfirmationBackToChatClicked(kind),
    checkoutConfirmationCloseClicked: (kind) => checkoutConfirmationCloseClicked(kind),
    // HORO-10504 — Zara upsell/welcome funnel
    zaraUpsellModalViewed: (isAnon) => zaraUpsellModalViewed(isAnon),
    zaraUpsellSubscribeClicked: (isAnon) => zaraUpsellSubscribeClicked(isAnon),
    zaraUpsellRegisterClicked: () => zaraUpsellRegisterClicked(),
    zaraWelcomeModalViewed: () => zaraWelcomeModalViewed(),
    // HORO-10474 — client-side API failures
    apiError: (params) => apiError(params),
    // HORO-10370 — session_end payload builder (trigger wiring deferred)
    sessionEnd: (opts) => sessionEnd(opts),
    // HORO-10370 — super-property setters
    setCurrentAdvisor: (advisor) => setCurrentAdvisor(advisor),
    setUserState: (state) => setUserState(state),
    setUserTier: (plan) => setUserTier(plan),
    setLoginProvider: (provider) => setLoginProvider(provider),
    initSession: () => initSession(),
    trackEvent: (name, params) => trackEvent(name, params)
};

export { gaEvents };
